import { mdiAlertCircleOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { CircularProgress } from '@mui/material'
import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { NavLink } from 'react-router'
import { firstValueFrom, zip } from 'rxjs'
import { setTikTokProfileSettings } from 'services/compose'
import { getTikTokProfileSettings } from 'services/compose/tiktok/actions'
import { getTikTokProfilePrivacyOptions } from 'services/compose/tiktok/net'
import {
  emptyTikTokSettings, hasSavedTikTokSettings, normalizeTikTokPrivacyOptions, reconcileTikTokSettings
} from 'services/compose/tiktok/utils'
import { HTTP_STATUS_UNAUTHORIZED, PostDestination, TikTokPostProfileSettings, TikTokPrivacyOptions } from 'shared'
import { StoreThunkDispatch } from 'store/state'
import styles from './TikTokOptions.pcss'
import { TikTokPostSettings } from './TikTokPostSettings'

interface TikTokProfileSettingsProps {
  profile: PostDestination
  isPhotoPost: boolean
  settings?: TikTokPostProfileSettings
  options?: TikTokPrivacyOptions
  className?: string
  showLoader?: boolean
  skipFetchSettings?: boolean
  ignoreSavedSettings?: boolean
  hideSaveOption?: boolean
  /** For callers that fetched the settings themselves (`skipFetchSettings`) and know whether a saved record exists. */
  hasSavedSettings?: boolean
  showPhotoOptions?: boolean
  onChange?: (settings: TikTokPostProfileSettings) => void
}

export const TikTokProfileSettings = memo(function TikTokProfileSettings(props: TikTokProfileSettingsProps) {
  const { profile, settings, className, showLoader, ignoreSavedSettings, hideSaveOption, skipFetchSettings, onChange } = props
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [fetchedOptions, setFetchedOptions] = useState<TikTokPrivacyOptions | null>(null)
  const [fetchedSavedRecord, setFetchedSavedRecord] = useState<boolean | undefined>(undefined)
  const [fetchError, setFetchError] = useState<null | React.ReactNode>(null)
  // Starts true when a fetch is about to run: `settings` is still empty on the first paint, and the validation error
  // derived from it would flash before the effect below flips this.
  const [loading, setLoading] = useState(!skipFetchSettings)
  // The fetch effect below reads these asynchronously and must not re-run when they change, otherwise a
  // caller passing an unmemoized `onChange` would refetch on every one of its renders.
  const latest = useRef({ settings, onChange, isPhotoPost: props.isPhotoPost })

  useEffect(() => {
    latest.current = { settings, onChange, isPhotoPost: props.isPhotoPost }
  }, [settings, onChange, props.isPhotoPost])

  // Derived, not state: recomputing during render avoids the extra pass an effect + setState would cost.
  const validationError = !settings?.privacyLevel
    ? 'Privacy level not selected!'
    : !settings.promoting ? 'Promotion type not selected!' : null
  const error = fetchError || validationError

  const defaultSettings = useMemo(() => emptyTikTokSettings(profile.ppid), [profile.ppid])

  useEffect(() => {
    if (skipFetchSettings) {
      return
    }
    let cancelled = false
    setLoading(true)
    firstValueFrom(zip(dispatch(getTikTokProfilePrivacyOptions(profile.ppid)), dispatch(getTikTokProfileSettings(profile.ppid))))
      .then(([options, saved]) => {
        if (cancelled) {
          return
        }
        const nextOptions = normalizeTikTokPrivacyOptions(options.settings)

        setFetchedOptions(nextOptions)
        setFetchedSavedRecord(hasSavedTikTokSettings(saved))
        setFetchError(null)

        const updatedSettings = reconcileTikTokSettings({
          ppid: profile.ppid,
          options: nextOptions,
          saved,
          current: latest.current.settings,
          isPhotoPost: latest.current.isPhotoPost,
          ignoreSavedSettings
        })

        if (latest.current.onChange) {
          latest.current.onChange(updatedSettings)
        } else {
          dispatch(setTikTokProfileSettings(updatedSettings))
        }
      })
      .catch(e => {
        if (cancelled) {
          return
        }
        if (e.response?.error?.code === HTTP_STATUS_UNAUTHORIZED && e.response.error.data?.type === 'tiktok') {
          setFetchError(
            <Fragment>
              <span>We could not load the privacy settings for <b>{profile.name}</b>. </span>
              <span>
                <NavLink to="/settings/networks">Reset your TikTok connection</NavLink> and allow all permissions Post Planner asks for.
              </span>
            </Fragment>
          )
        } else {
          setFetchError(
            <Fragment>
              We could not load your privacy settings for <b>{profile.name}</b>. Please try again later.
            </Fragment>
          )
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [dispatch, profile.name, profile.ppid, ignoreSavedSettings, skipFetchSettings])

  const profileOptions = fetchedOptions || props.options
  // While the saved settings are being ignored they are not restored either, so the box must not offer to overwrite
  // them from this post - it starts unchecked and only a deliberate tick persists anything.
  const savedRecordExists = ignoreSavedSettings ? false : fetchedSavedRecord ?? props.hasSavedSettings

  return (
    <div className={`${styles['profile-settings']} ${className || ''}`} data-testid="tiktok-profile-settings">
      {profileOptions && (
        <TikTokPostSettings
          profile={profile}
          settings={settings || defaultSettings}
          options={profileOptions}
          hideSaveOption={hideSaveOption}
          hasSavedSettings={savedRecordExists}
          isPhotoPost={props.isPhotoPost}
          showPhotoOptions={props.showPhotoOptions}
          onChange={onChange}
        />
      )}
      {error && !loading && (
        <div className={styles.error}>
          <Icon path={mdiAlertCircleOutline} size="20px" color="#ff7676" />
          <span>{error}</span>
        </div>
      )}
      {loading && showLoader && (
        <div className={styles['loader-box']}>
          <CircularProgress size={20} />
        </div>
      )}
    </div>
  )
})
